import { ref, onMounted, onBeforeUnmount } from 'vue';

/*
this composable logic is as follows:

On mount it listens on window for mousemove, mousedown, keydown, scroll, touchstart, wheel and click, and starts a 60-second countdown.

Any of those events resets the countdown and sets isIdle = false.

When the countdown runs out, isIdle becomes true.

Switching to another tab (visibilitychange with document.hidden) makes the user idle immediately. Coming back to the tab counts as activity.

IMPORTANT!
A mousemove with the same coordinates as the last one is ignored. Without this, the table re-rendering under a cursor that hasn't moved would fire a fake mousemove, and the user would never go idle. 
*/

export interface UseIdleDetectionOptions {
  /** Milliseconds of no activity before the user is considered idle. */
  timeout?: number;
  /** DOM events on `window` that count as user activity. */
  events?: string[];
  /** Begin detecting as soon as the component mounts (default true). */
  immediate?: boolean;
  /** Treat a hidden/backgrounded tab as idle immediately (default true). */
  detectVisibility?: boolean;
  /** Treat the browser window losing focus (switching to another app) as idle immediately (default true). */
  detectWindowFocus?: boolean;
  /** Called once each time the user transitions into the idle state. */
  onIdle?: () => void;
  /** Called once each time the user transitions back to active. */
  onActive?: () => void;
}

const DEFAULT_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
  'click',
];

/**
 * Pluggable idle detection. Returns a reactive `isIdle` flag plus `start`/`stop`
 * so any component can turn detection on or off at will. Options API components
 * can call this in `setup()` and watch the returned `isIdle`; Composition API
 * components can pass `onIdle`/`onActive` callbacks instead.
 */
export function useIdleDetection(options: UseIdleDetectionOptions = {}) {
  const {
    timeout = 60_000,
    events = DEFAULT_EVENTS,
    immediate = true,
    detectVisibility = true,
    detectWindowFocus = true,
    onIdle,
    onActive,
  } = options;

  const isIdle = ref(false);
  const lastActiveAt = ref(Date.now());
  let timer: ReturnType<typeof setTimeout> | null = null;
  let isListening = false;
  let lastMouseX = -1;
  let lastMouseY = -1;

  function goIdle() {
    if (isIdle.value) return;
    // console.log('ACTIVE => idle', new Date());
    isIdle.value = true;
    onIdle?.();
  }

  function goActive() {
    lastActiveAt.value = Date.now();
    if (isIdle.value) {
      // console.log('idle => ACTIVE', new Date());
      isIdle.value = false;
      onActive?.();
    }
  }

  /** Restart the no-activity countdown. */
  function resetTimer() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(goIdle, timeout);
  }

  /** Any tracked activity marks the user active and restarts the countdown. */
  function onActivity(event?: Event) {
    // A DOM re-render under a stationary cursor fires a `mousemove` with the
    // same coordinates. Ignore those, otherwise the periodic background refresh
    // keeps resetting the countdown and the user never goes idle. Only real
    // pointer movement counts.
    if (event && event.type === 'mousemove') {
      const mouseEvent = event as MouseEvent;
      if (mouseEvent.clientX === lastMouseX && mouseEvent.clientY === lastMouseY) {
        return;
      }
      lastMouseX = mouseEvent.clientX;
      lastMouseY = mouseEvent.clientY;
    }
    goActive();
    resetTimer();
  }

  function onVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      // console.log('document is hidden')
      // Backgrounded tab → idle right away, no countdown.
      if (timer) clearTimeout(timer);
      goIdle();
    } else {
      onActivity();
    }
  }

  /*
   * ************************************************************************
   * **NOTE** window `blur`/`focus` — KNOWN SIDE EFFECTS
   *
   * `visibilitychange` only fires when the tab is hidden (tab switch, minimize,
   * screen lock). Switching to ANOTHER APP leaves the page "visible", so we also
   * listen to window `blur`/`focus`. Be aware:
   *
   * **IFRAMES:** clicking into an iframe on the page (and clicking into DevTools)
   * fires window `blur`, so the user goes idle immediately even though they are
   * still using the page.
   *
   * **MAC HOVER:** on macOS, hovering over an unfocused browser window still
   * delivers `mousemove` to the page. So after a `blur` → idle, just moving the
   * mouse over the browser (no click, window still unfocused) flips the user
   * back to active and restarts the countdown.
   * ************************************************************************
   */
  function onWindowBlur() {
    // Another app (or an iframe / DevTools) took focus → idle right away, no countdown.
    if (timer) clearTimeout(timer);
    goIdle();
  }

  function onWindowFocus() {
    onActivity();
  }

  /** Attach listeners and begin the countdown. Safe to call repeatedly. */
  function start() {
    if (isListening || typeof window === 'undefined') return;
    // console.log('starting idle detection', new Date());
    isListening = true;
    events.forEach((evt) =>
      // IMPORTANT to keep passive: true
      // tells the browser "don't bother waiting — just scroll (or some other event) immediately," 
      // without blocking the main thread while waiting on handler to complete
      // so it can run the scroll (or whatever the event) on a separate thread while your handler runs in parallel.
      window.addEventListener(evt, onActivity, { passive: true }),
    );
    if (detectVisibility && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    if (detectWindowFocus) {
      window.addEventListener('blur', onWindowBlur);
      window.addEventListener('focus', onWindowFocus);
    }
    goActive();
    resetTimer();
  }

  /** Detach listeners and stop tracking. Safe to call repeatedly. */
  function stop() {
    if (!isListening) return;
    // console.log('stopping idle detection', new Date());
    isListening = false;
    events.forEach((evt) => window.removeEventListener(evt, onActivity));
    if (detectVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (detectWindowFocus) {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    }
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  if (immediate) {
    onMounted(() => {
      // console.log('on mount: useIdleDetection: starting', new Date());
      start();
    });
  }
  onBeforeUnmount(() => {
    // console.log('on unmount: useIdleDetection: stopping', new Date());
    stop();
  });

  return { isIdle, lastActiveAt, start, stop };
}
